import React, { useEffect, useRef, useState } from 'react';
import { Shield } from 'lucide-react';
import animationManager from '../utils/AnimationManager';

/**
 * HeroSection - Animated hero banner with ASCII art and typing effects
 */
const HeroSection = () => {
  const heroRef = useRef(null);
  const [displayedText, setDisplayedText] = useState('');
  const tagline = "Penetrating systems. Securing networks. Hunting threats.";

  useEffect(() => {
    if (!heroRef.current || !animationManager.shouldAnimate()) {
      setDisplayedText(tagline);
      return;
    }

    // Hero entrance animation
    const timeline = animationManager.createTimeline('hero-section');
    
    timeline.add({
      targets: heroRef.current,
      opacity: [0, 1],
      translateY: [-50, 0],
      duration: 800,
      easing: 'easeOutCubic'
    });

    // Typing effect for tagline
    let index = 0;
    const typingInterval = setInterval(() => {
      if (index <= tagline.length) {
        setDisplayedText(tagline.substring(0, index));
        index++;
      } else {
        clearInterval(typingInterval);
      }
    }, 50);

    // Pulsing glow on shield icon
    const shield = heroRef.current.querySelector('.hero-shield');
    if (shield) {
      animationManager.pulse(shield, { scale: [1, 1.1, 1] });
    }

    return () => {
      clearInterval(typingInterval);
      animationManager.removeTimeline('hero-section');
    };
  }, []);

  return (
    <div
      ref={heroRef}
      className="mb-3 sm:mb-4 md:mb-6 text-center opacity-0 px-2"
      style={{ fontFamily: "'Orbitron', sans-serif" }}
    >
      {/* Animated Shield Icon */}
      <div className="flex justify-center mb-2 sm:mb-3 md:mb-4">
        <div className="hero-shield relative">
          <Shield className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-[#00ff41]" strokeWidth={1.5} />
          <div className="absolute inset-0 bg-[#00ff41]/20 blur-xl rounded-full animate-pulse" />
        </div>
      </div>

      {/* Main Title */}
      <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00ff41] mb-2 sm:mb-3 tracking-wider">
        DEEZSEC
      </h1>

      {/* Subtitle with typing effect */}
      <div className="text-xs sm:text-sm md:text-lg lg:text-xl text-[#00ff41]/80 mb-2 sm:mb-3 md:mb-4 h-6 sm:h-7 md:h-8 font-mono">
        {displayedText}
        <span className="animate-pulse">_</span>
      </div>

      {/* Access Status Indicator */}
      <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-[#00ff41]/10 border border-[#00ff41]/30 rounded-lg">
        <div className="relative">
          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#00ff41] rounded-full animate-pulse" />
          <div className="absolute inset-0 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#00ff41] rounded-full animate-ping" />
        </div>
        <span className="text-[10px] sm:text-xs text-[#00ff41] font-semibold tracking-wider">
          <span className="hidden sm:inline">ACCESS GRANTED • SYSTEM ONLINE</span>
          <span className="sm:hidden">ONLINE</span>
        </span>
      </div>

      {/* Stats Row */}
      <div className="mt-3 sm:mt-4 md:mt-6 flex justify-center gap-4 sm:gap-6 md:gap-8 text-xs sm:text-sm">
        <div className="flex flex-col items-center">
          <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#00ff41]">15+</div>
          <div className="text-[#00ff41]/60 text-[10px] sm:text-xs md:text-sm">Projects</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#9d4edd]">7+</div>
          <div className="text-[#9d4edd]/60 text-[10px] sm:text-xs md:text-sm">Skills</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#00d9ff]">∞</div>
          <div className="text-[#00d9ff]/60 text-[10px] sm:text-xs md:text-sm">Threats Hunted</div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
